import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { AccountCard, AccountForm } from "@/components/finance"
import { OrganizationGuard } from "@/components/organization-guard"
import { CardsSkeleton } from "@/components/skeletons/cards-skeleton"
import { Button } from "@/components/ui/button"
import {
  deleteAccount,
  getAccountsSummary,
  type TAccountsSummary,
} from "@/http/finance"
import { useOrganization } from "@/store/organization"

const accountsSummaryQueryOptions = (
  orgSlug: string,
  month: number,
  year: number,
) =>
  queryOptions({
    queryKey: ["accounts-summary", orgSlug, month, year],
    queryFn: async () => {
      const response = await getAccountsSummary(orgSlug, month, year)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

/**
 * Finance accounts route
 */
export const Route = createFileRoute(
  "/_authenticated/$orgSlug/settings/finance/accounts",
)({
  loader: async ({ context, params }) => {
    const now = new Date()
    await context.queryClient.ensureQueryData(
      accountsSummaryQueryOptions(
        params.orgSlug,
        now.getMonth() + 1,
        now.getFullYear(),
      ),
    )
  },
  component: FinanceAccounts,
  pendingComponent: CardsSkeleton,
})

export function FinanceAccounts() {
  const { currentOrg } = useOrganization()
  const { orgSlug } = Route.useParams()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedAccount, setSelectedAccount] =
    useState<TAccountsSummary | null>(null)

  // Fetch accounts
  const {
    data: { summary },
    refetch,
  } = useSuspenseQuery(
    accountsSummaryQueryOptions(
      orgSlug,
      new Date().getMonth() + 1,
      new Date().getFullYear(),
    ),
  )

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const response = await deleteAccount({
        organizationSlug: currentOrg?.slug ?? "",
        accountId,
      })

      if (response.error) {
        throw new Error(response.error.message)
      }
    },
    onSuccess: () => {
      refetch()
      toast.success("Conta removida com sucesso!")
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao remover conta")
    },
  })

  const handleDelete = (accountId: string) => {
    if (confirm("Tem certeza que deseja remover esta conta?")) {
      deleteMutation.mutate(accountId)
    }
  }

  const handleEdit = (accountId: string) => {
    const account = summary?.find((a) => a.id === accountId)
    if (account) {
      setSelectedAccount(account)
      setEditDialogOpen(true)
    }
  }

  if (!currentOrg) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Contas</h2>
          <p className="text-muted-foreground">
            Gerencie suas contas financeiras
          </p>
        </div>
        <OrganizationGuard action="create" subject="FinanceAccount">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Conta
          </Button>
        </OrganizationGuard>
      </div>

      {/* Accounts Grid */}
      {summary && summary.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {summary.map((account) => (
            <OrganizationGuard
              key={account.id}
              action="delete"
              subject="FinanceAccount"
              fallback={
                <OrganizationGuard
                  action="update"
                  subject="FinanceAccount"
                  fallback={
                    <AccountCard
                      account={account}
                      canDelete={false}
                      canEdit={false}
                    />
                  }
                >
                  <AccountCard
                    account={account}
                    canDelete={false}
                    canEdit={true}
                    onEdit={handleEdit}
                  />
                </OrganizationGuard>
              }
            >
              <OrganizationGuard
                action="update"
                subject="FinanceAccount"
                fallback={
                  <AccountCard
                    account={account}
                    canDelete={true}
                    canEdit={false}
                    onDelete={handleDelete}
                  />
                }
              >
                <AccountCard
                  account={account}
                  canDelete={true}
                  canEdit={true}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              </OrganizationGuard>
            </OrganizationGuard>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <p>Nenhuma conta cadastrada ainda.</p>
          <p className="text-sm mt-2">
            Crie sua primeira conta para começar a gerenciar suas finanças.
          </p>
        </div>
      )}

      {/* Create Dialog */}
      <AccountForm
        organizationSlug={currentOrg.slug}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          refetch()
          toast.success("Conta criada com sucesso!")
        }}
      />
      {selectedAccount && (
        <AccountForm
          account={selectedAccount}
          organizationSlug={currentOrg.slug}
          open={editDialogOpen}
          onOpenChange={() => {
            setEditDialogOpen(false)
            setSelectedAccount(null)
          }}
          onSuccess={() => {
            refetch()
            setSelectedAccount(null)
            toast.success("Conta atualizada com sucesso!")
          }}
        />
      )}
    </div>
  )
}
